/**
 * Recent chats, as a rail (design: "Led AI Console", the Ask screen).
 *
 * ## What it replaces
 *
 * A "Recent asks" button under the title that dropped a panel over the
 * conversation. That was the phone's history OVERLAY ported literally, and
 * the design's answer for a console is the opposite: a persistent column, so
 * the list of what you have asked is context you read alongside the answer
 * rather than something you open, read, and dismiss to get back.
 *
 * ## Collapsed to 48px, never to nothing
 *
 * The design keeps the rail present when it is shut — 48px holding the two
 * controls — rather than removing it. A rail that disappeared would take its
 * own re-open control with it, and the reader would be left looking for a
 * button that is no longer on the screen.
 *
 * ## What a row says
 *
 * The question, clamped to two lines, over one mono line of meta. The design
 * puts the project and a record count there ("66 Pearl · 2 records"). The
 * console cannot: an entry records the question, the answer and when — not
 * the scope it was asked under — and a project name we would have to infer is
 * exactly the kind of confident wrong answer this rail exists to help catch.
 * So the meta is the clock time (which is what tells two of today's asks
 * apart) and the distinct records cited in the latest answer, matching its
 * Sources list. Query and inventory totals describe different things.
 */

import type { AskHistoryEntry } from "../api";
import { C, FONT_MONO } from "../tokens";
import { META, clockOf, plural } from "./chrome";
import { groupAskHistory } from "../lib/askHistoryGroups";
import { citedRecordsInOrder } from "./ask/askCitations";
import { SkeletonRows } from "./Skeleton";

/** The design's two widths. */
export const ASK_RAIL_WIDTH = 264;
export const ASK_RAIL_COLLAPSED_WIDTH = 48;

export function AskHistoryRail({
  entries,
  selectedId,
  open,
  onToggle,
  onSelect,
  onNew,
  problem,
  now,
  loading = false,
}: {
  entries: AskHistoryEntry[];
  /** The exchange on screen, when it came from this list. */
  selectedId: string | null;
  open: boolean;
  onToggle: () => void;
  onSelect: (entry: AskHistoryEntry) => void;
  /** Clear the conversation and start a new one. */
  onNew: () => void;
  /** Why the list could not be read, when it could not. */
  problem: string | null;
  /** The clock the day buckets are measured against. */
  now: Date;
  loading?: boolean;
}) {
  const groups = groupAskHistory(entries, now);
  return (
    <div
      data-testid="ask-rail"
      data-open={open ? "true" : "false"}
      style={{
        flex: "none",
        width: open ? ASK_RAIL_WIDTH : ASK_RAIL_COLLAPSED_WIDTH,
        // The design gives the rail the PAGE's ground, not a white column:
        // Ask is one continuous surface with a rule down it, and the only
        // white on the screen is the composer card and the cards under it.
        // A white rail made the history the brightest thing on a page whose
        // subject is the answer beside it.
        background: "transparent",
        borderRight: `1px solid ${C.railEdge}`,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        overflow: "hidden",
        minHeight: 0,
        alignSelf: "stretch",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 64,
          padding: open ? "0 10px 0 14px" : "0 9px",
          borderBottom: `1px solid ${C.line}`,
          flex: "none",
        }}
      >
        {open && (
          <>
            <span
              style={{
                ...META,
                flex: 1,
                fontSize: 10.93,
                letterSpacing: "0.1em",
                color: C.propLabel,
                whiteSpace: "nowrap",
              }}
            >
              Recent chats
            </span>
            <NewAskButton onClick={onNew} />
          </>
        )}
        <button
          type="button"
          className="kb"
          data-testid="ask-rail-toggle"
          aria-expanded={open}
          aria-label={open ? "Hide recent chats" : "Show recent chats"}
          title={open ? "Hide recent chats" : "Show recent chats"}
          onClick={onToggle}
          style={{
            width: 30,
            height: 30,
            flex: "none",
            borderRadius: 8,
            border: "none",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <span
            aria-hidden
            style={{ display: "inline-flex", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 6l6 6-6 6"
                stroke={C.propLabel}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </div>

      {open ? (
        <div
          data-testid="ask-rail-list"
          className="sb"
          style={{
            flex: 1,
            // In a nested flex column, `minHeight: 0` permits shrinking but
            // is not enough in every browser to give the child a definite
            // scrollport. The zero basis makes this list own all remaining
            // rail height, exactly like Review Queue's walk list.
            height: 0,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            padding: "10px 8px 16px",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {loading && entries.length === 0 && <div role="status" aria-label="Loading recent chats" data-skeleton="ask-history"><SkeletonRows rows={4} height={64} gap={6} /></div>}
          {groups.map((group) => (
            <div key={group.label}>
              <div
                data-testid="ask-rail-day"
                style={{
                  ...META,
                  fontSize: 10.35,
                  letterSpacing: "0.1em",
                  color: C.railMeta,
                  padding: "12px 8px 6px",
                }}
              >
                {group.label}
              </div>
              {group.entries.map((entry) => (
                <RailRow
                  key={entry.id}
                  entry={entry}
                  selected={entry.id === selectedId}
                  onSelect={() => onSelect(entry)}
                />
              ))}
            </div>
          ))}

          {groups.length === 0 && !problem && !loading && (
            <div
              data-testid="ask-rail-empty"
              style={{ padding: "12px 10px", fontSize: 14.37, lineHeight: 1.5, color: C.sub }}
            >
              You have not asked Led anything yet. Whatever you ask lands here.
            </div>
          )}
          {problem && (
            // Not an empty list. A history that could not be read and rendered
            // as "nothing here" would quietly tell the reader they had never
            // asked anything.
            <div
              data-testid="ask-rail-unavailable"
              style={{
                margin: "6px 2px",
                padding: "11px 12px",
                borderRadius: 10,
                background: C.redSoft,
                fontSize: 14.37,
                lineHeight: 1.5,
              }}
            >
              {problem}
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            padding: "10px 0",
          }}
        >
          <NewAskButton onClick={onNew} />
          {/* A second way back to the list, on the rail itself. The chevron
              in the header above does the same thing, and the design carries
              both: 48px of empty column with one `+` in it does not read as
              "there is a history behind this". */}
          <button
            type="button"
            className="kb"
            data-testid="ask-rail-reopen"
            aria-label="Show recent chats"
            title="Recent chats"
            onClick={onToggle}
            style={{
              width: 30,
              height: 30,
              flex: "none",
              borderRadius: 8,
              border: "none",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M4 6h16M4 12h16M4 18h16"
                stroke={C.propLabel}
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

function NewAskButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="kb"
      data-testid="ask-rail-new"
      aria-label="New chat"
      title="New chat"
      onClick={onClick}
      style={{
        width: 30,
        height: 30,
        flex: "none",
        borderRadius: 8,
        background: C.surface,
        border: `1px solid ${C.railControl}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        cursor: "pointer",
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 5v14M5 12h14"
          stroke={C.railScope}
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

function RailRow({
  entry,
  selected,
  onSelect,
}: {
  entry: AskHistoryEntry;
  selected: boolean;
  onSelect: () => void;
}) {
  const cited = citedRecordsInOrder(entry.answer?.answer ?? "", entry.answer?.references ?? []).length;
  return (
    <button
      type="button"
      className="kb"
      data-testid="ask-rail-entry"
      data-entry={entry.id}
      data-selected={selected ? "true" : undefined}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        minWidth: 0,
        textAlign: "left",
        padding: "9px 10px",
        borderRadius: 9,
        border: "none",
        background: selected ? C.weekSelected : "transparent",
        font: "inherit",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          fontSize: 15.52,
          fontWeight: 500,
          lineHeight: 1.3,
          color: C.propValue,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {entry.query}
      </span>
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 10.35,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: C.railMeta,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {`${clockOf(new Date(entry.createdAt))} · ${
          cited > 0 ? `${plural(cited, "record", "records")} cited` : "no records cited"
        }`}
      </span>
    </button>
  );
}
